import { AsyncPipe, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router, RouterOutlet } from '@angular/router';
import { environment } from '@environments/environment';
import { Actions, ofType } from '@ngrx/effects';
import { paths } from '@platform/paths';
import { NavigationHeaderRadiusContainerComponent } from '@shared/components/navigation-header-radius-container/navigation-header-radius-container.component';
import { DEFAULT_LOGO_URL } from '@shared/contants';
import { AuthService } from '@shared/services/auth.service';
import { getAuthPagesBrandingFailure, getAuthPagesBrandingSuccess } from '@shared/store/auth/auth.action';
import { resetSignUpBrandingColors } from '@shared/store/sign-up/branding/utils/reset-sign-up-branding-colors';
import { AuthFacadeService } from './auth-facade.service';

@Component({
  selector: 'cp-auth',
  templateUrl: './auth.component.html',
  providers: [AuthFacadeService],
  imports: [AsyncPipe, RouterOutlet, NavigationHeaderRadiusContainerComponent, NgTemplateOutlet, NgOptimizedImage],
  styleUrls: ['./auth.component.scss'],
})
export class AuthComponent implements OnInit {
  private readonly facade = inject(AuthFacadeService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly actions$ = inject(Actions);

  readonly authPagesBranding$ = this.facade.authPagesBranding$;
  readonly isLoginFromParamsLoading$ = this.facade.isLoginFromParamsOrBrandingLoading$;
  readonly mobileAppLogo = environment.mobileApp?.logoUrl;
  readonly defaultLogoUrl = DEFAULT_LOGO_URL;

  ngOnInit(): void {
    resetSignUpBrandingColors();
    this.subscribeBranding();
    this.subscribeMobileAppDeepLinks();
  }

  private subscribeBranding(): void {
    this.actions$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(getAuthPagesBrandingSuccess, getAuthPagesBrandingFailure))
      .subscribe(() => {
        this.handleUrlParams();
      });
  }

  private subscribeMobileAppDeepLinks(): void {
    if (!environment.mobileApp?.isMobileApp) return;
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params['appDeepLink']) this.handleUrlParams();
    });
  }

  private handleUrlParams(): void {
    const params = this.route.snapshot.queryParams;
    if (params['token']) {
      this.loginWithToken(params); // impersonate user => send token directly
    } else {
      this.handleAccessCodeLogin(params);
    }
  }

  private handleAccessCodeLogin(params: Params): void {
    if (!params['data'] || this.route.firstChild?.routeConfig?.path !== paths.auth.AUTH_CODE) return;

    this.setAccessCode(new URLSearchParams(atob(params['data'])));
  }

  private loginWithToken(params: Params): void {
    this.authService.logout();
    this.authService.setImpersonated();
    this.facade.loginWithToken(params['token'], params['link']);
  }

  private setAccessCode(decodedParams: URLSearchParams): void {
    const redirectUrl = decodedParams.get('redirectUrl');
    const code = decodedParams.get('code');
    if (!code || !redirectUrl) return;

    this.authService.setAccessCode(code);
    this.router.navigateByUrl(redirectUrl);
  }
}
